<template>
  <el-form-item
    :error="errors.prices || errors.cpms"
    :rules="{
      required: true,
      message: `Please enter at least one ${priceLabel}`,
      trigger: 'blur'
    }"
    label="Prices by country"
    size="small"
  >
    <div style="width: 100%">
      <el-input-number
        v-model="cpmRate"
        controls-position="right"
        :min="0"
        :step="0.1"
      />
      <el-button class="ml-1" type="primary" plain @click.prevent="setValue">
        Set
      </el-button>
      <div class="input-hint">Set rate for all countries</div>
    </div>
    <el-table
      :data="countries"
      :style="{ width: tableWidth + 'px' }"
      max-height="400"
      cell-class-name="cell-class-country-prices"
      header-cell-class-name="cell-class-country-prices"
      class-name="table-country-prices"
    >
      <el-table-column prop="country" label="Country" />
      <template v-if="!cpcBillimgMode">
        <el-table-column
          prop="recommended"
          label="Recommended CPM, $"
          width="180"
          class-name="recommended-cpm-column"
          label-class-name="recommended-cpm-column"
        >
          <template #default="{ row }">
            <el-tooltip
              effect="dark"
              placement="top"
              content="Average CPM for the chosen geo and device. You may set your CPM as recommended or higher to get more traffic. Gets updated daily."
            >
              <span style="font-weight: bold">
                {{ row.recommended.toFixed(3) }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </template>
      <el-table-column :label="`${priceLabel}, $`" width="150">
        <template #default="scope">
          <CountryPriceInput
            :value="scope.row.price"
            :skip-cpm-check="skipCpmCheck"
            :country="scope.row.iso"
            :min="scope.row.minimal"
            :max="scope.row.maximal"
            :validation-error="bulkValidationErrors[scope.row.iso]"
            @update:modelValue="update"
          />
        </template>
      </el-table-column>
      <template v-if="ratesVisible">
        <el-table-column prop="rates" label="Win Rates" width="750" />
      </template>
    </el-table>
  </el-form-item>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import useValidation from '@/hooks/useValidation'
import {
  CreativeValidationErrorsKey,
  ICountryCPM,
  IWinPrices
} from '@/api/types'
import { ICountry } from '@/api/countries'
import { useCountriesStore } from '@/store/modules/countries'
import { useDspMinimalCpmsStore } from '@/apps/traforama/store/dspMinimalCpms'
import { useRecommendedPricesStore } from '@/apps/traforama/store/recomendedPrices'
import CountryPriceInput from './CountryPriceInput.vue'
import useFetchCampaign from '@/hooks/useFetchCampaign'
import chunk from '@/utils/array'
import useResize from '@/hooks/useResize'
import { useUserStore } from '@/store/modules/user'
import {
  cpmRangeError,
  recommendedCpmFromHistogram
} from '@/apps/traforama/views/creatives/utils/cpm'
export default defineComponent({
  name: 'CountryPrices',
  components: {
    CountryPriceInput
  },
  props: {
    cpc: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Array,
      required: true
    },
    winPrices: {
      type: Array,
      default: () => []
    },
    useHistoricalPrices: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()
    const recommendedPricesStore = useRecommendedPricesStore()
    const dspMinimalCpmsStore = useDspMinimalCpmsStore()
    const { validationErrors } = useValidation(CreativeValidationErrorsKey)
    const errors = validationErrors()
    const { injectCampaign } = useFetchCampaign()
    const campaign = injectCampaign()
    const { isDesktop } = useResize()
    const cpmRate = ref(0)
    const bulkValidationErrors = ref<Record<string, string>>({})
    const cpcBillimgMode = computed(() => props.cpc)
    const priceLabel = computed(() => {
      return cpcBillimgMode.value ? 'CPC' : 'CPM'
    })

    const skipCpmCheck = computed(() => {
      return userStore.skipCpmCheck || cpcBillimgMode.value
    })

    context.emit('validation-state-change', false)
    watch(skipCpmCheck, (skipCheck) => {
      if (!skipCheck) return

      bulkValidationErrors.value = {}
      context.emit('validation-state-change', false)
    })

    const update = (newValue: ICountryCPM) => {
      if (bulkValidationErrors.value[newValue.id]) {
        const updatedErrors = { ...bulkValidationErrors.value }
        delete updatedErrors[newValue.id]
        bulkValidationErrors.value = updatedErrors
        context.emit(
          'validation-state-change',
          Object.keys(updatedErrors).length > 0
        )
      }

      const newPrices = [
        ...(props.modelValue as Array<ICountryCPM>).filter(
          (el) => el.id !== newValue.id
        ),
        newValue
      ]
      context.emit('update:modelValue', newPrices)
    }

    const countriesStore = useCountriesStore()
    const countriesReady = countriesStore.GetItems()
    // @ts-ignore: Object is possibly 'undefined'
    const dspReady = dspMinimalCpmsStore.FetchItems(campaign.value.type_id)
    // @ts-ignore: Object is possibly 'undefined'
    const recommendedReady = recommendedPricesStore.FetchItems(
      campaign.value.id
    )
    const ratesVisible =
      userStore.isStaff && props.winPrices && props.winPrices.length > 0
    // to save table small if no rates present
    const tableWidth = ratesVisible ? 1180 : 580
    const countries = computed(() => {
      // @ts-ignore: Object is possibly 'undefined'
      const campaignCountries = campaign.value.country_codes
      let targetCountries = countriesStore.sorted
      if (campaignCountries && campaignCountries.length) {
        targetCountries = countriesStore.byIso(campaignCountries)
      }
      const minimals = dspMinimalCpmsStore.items
      const recommendedPrices = recommendedPricesStore.items
      return targetCountries
        .map((country: ICountry) => {
          const countryCPM = (props.modelValue as Array<ICountryCPM>).find(
            (el) => el.id === country.attributes.iso
          )
          const rates = (props.winPrices as Array<IWinPrices>).find(
            (el) => el.country === country.attributes.iso
          )
          const ratesString = rates
            ? rates.data
                .map((item) => `${item.win_rate}% - ${item.price.toFixed(4)}$`)
                .join(', ')
            : ''
          const price = countryCPM ? countryCPM.value : ''
          const minimal = minimals[country.id]
            ? minimals[country.id].attributes.value
            : 0.0
          const maximal = minimals[country.id]
            ? minimals[country.id].attributes.value_max
            : 0.0
          const baseRecommended = recommendedPrices[country.id]
            ? recommendedPrices[country.id].value
            : 0.0
          const historicalRecommended =
            props.useHistoricalPrices && rates
              ? recommendedCpmFromHistogram(rates.data)
              : undefined
          let recommended = historicalRecommended ?? baseRecommended
          recommended =
            recommended <= minimal || recommended === 0 ? minimal : recommended

          return {
            id: country.id,
            country: country.attributes.name,
            iso: country.attributes.iso,
            price: price,
            minimal: minimal,
            maximal: maximal,
            recommended: recommended,
            rates: ratesString
          }
        })
        .filter((c: any) => {
          if (Object.keys(recommendedPrices).length === 0) return true
          return c.minimal > 0 || c.recommended > 0 || c.maximal > 0
        })
        .sort((a: any, b: any) => {
          const nameA = a.country
          const nameB = b.country
          if (nameA < nameB) return -1
          if (nameA > nameB) return 1
          return 0
        })
    })

    watch(
      countries,
      (rows) => {
        context.emit(
          'recommendations-change',
          rows.map((row) => ({
            country: row.country,
            iso: row.iso,
            recommended: row.recommended
          }))
        )
      },
      { immediate: true }
    )

    // @ts-ignore: Object is possibly 'undefined'
    const campaignCountries = campaign.value.country_codes
    if (campaignCountries.length) {
      const filteredPrices = (props.modelValue as Array<ICountryCPM>).filter(
        (el) => campaignCountries.includes(el.id)
      )
      context.emit('update:modelValue', filteredPrices)
    }

    Promise.all([countriesReady, dspReady, recommendedReady]).then(() => {
      if ((props.modelValue as Array<ICountryCPM>).length > 0) return
      const values = countries.value
        .filter((c: any) => c.recommended > 0)
        .map((c: any) => ({ id: c.iso, value: c.recommended }))
      if (values.length) {
        context.emit('update:modelValue', values)
      }
    })

    const setValue = async () => {
      // @ts-ignore: Object is possibly 'undefined'
      const campaignCountries = campaign.value.country_codes
      let targetCountries = countriesStore.sorted
      if (campaignCountries && campaignCountries.length) {
        targetCountries = countriesStore.byIso(campaignCountries)
      }
      const valuesByCountry = new Map(
        (props.modelValue as Array<ICountryCPM>).map((price) => [
          price.id,
          price
        ])
      )
      const validationErrors: Record<string, string> = {}

      targetCountries.forEach((country: ICountry) => {
        const minimal = dspMinimalCpmsStore.items[country.id]
          ? dspMinimalCpmsStore.items[country.id].attributes.value
          : 0.0
        const maximal = dspMinimalCpmsStore.items[country.id]
          ? dspMinimalCpmsStore.items[country.id].attributes.value_max
          : 0.0
        const iso = country.attributes.iso

        if (
          skipCpmCheck.value ||
          (minimal <= cpmRate.value && maximal >= cpmRate.value)
        ) {
          valuesByCountry.set(iso, { id: iso, value: cpmRate.value })
        } else {
          validationErrors[iso] = cpmRangeError(minimal, maximal)
        }
      })

      bulkValidationErrors.value = validationErrors
      context.emit(
        'validation-state-change',
        Object.keys(validationErrors).length > 0
      )
      await context.emit(
        'update:modelValue',
        Array.from(valuesByCountry.values())
      )
    }

    return {
      errors,
      update,
      countries,
      setValue,
      cpmRate,
      chunk,
      isDesktop,
      skipCpmCheck,
      cpcBillimgMode,
      priceLabel,
      ratesVisible,
      tableWidth,
      bulkValidationErrors
    }
  }
})
</script>

<style lang="scss">
td.recommended-cpm-column {
  background-color: #ecf5ff;
}
th.recommended-cpm-column {
  color: #409eff;
}
tr:hover td.recommended-cpm-column {
  background-color: #ecf5ff !important;
}
</style>
